// pages/Skills.jsx
import React, { useState, useRef, useEffect } from "react";
import api from "../../api";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardMedia,
  Button,
  TextField,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
  Snackbar,
  Alert
} from "@mui/material";
import { color, motion } from "framer-motion";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import {
  AccountCircle as AccountCircleIcon,
  FindInPage as LostFoundIcon,
  Lightbulb as SkillsIcon,
  Help as DoubtsIcon,
  Settings as SettingsIcon,
  Event as EventIcon
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

import LogoutNavbar from "../../components/LogoutNavbar";
import PostLoginFooter from "../../components/PostLoginFooter";
import GlobalSidebar from "../../components/GlobalSidebar";
import BackButton from "../../components/BackButton";

import { getFullImageUrl } from "../../middleware/Imagegetter";
import { getPreviewFileUrl } from "../../middleware/Filehelper";

export default function Skills({ embedded = false } = {}) {
  const footerRef = useRef(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const [projects, setProjects] = useState([]);
  const [notes, setNotes] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [search, setSearch] = useState("");

  // Snackbar state
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const projectRefs = {
    cse: useRef(null),
    ece: useRef(null),
    me: useRef(null),
    ee: useRef(null),
    ce: useRef(null),
  };

  const notesRefs = {
    cse: useRef(null),
    ece: useRef(null),
    me: useRef(null),
    ee: useRef(null),
    ce: useRef(null),
  };

  // Define sidebar items to pass to Navbar
  const sidebarItems = [
    { 
      label: 'Profile', 
      path: '/login/student/home', 
      action: () => navigate('/login/student/home'),
      icon: <AccountCircleIcon />
    },
    { 
      label: 'Lost and Found', 
      path: '/lostfound', 
      action: () => navigate('/lostfound'),
      icon: <LostFoundIcon />
    },
    { 
      label: 'Skills', 
      path: '/skills', 
      action: () => navigate('/skills'),
      icon: <SkillsIcon />,
      active: true
    },
    { 
      label: 'Doubts', 
      path: '/yourdoubts', 
      action: () => navigate('/yourdoubts'),
      icon: <DoubtsIcon />
    },
    { 
      label: 'Settings', 
      path: '/settings', 
      action: () => navigate('/settings'),
      icon: <SettingsIcon />
    },
    { 
      label: 'Events', 
      path: '/events', 
      action: () => navigate('/events'),
      icon: <EventIcon />
    },
  ];

  const scroll = (ref, direction) => {
    if (ref.current) {
      ref.current.scrollBy({
        left: direction === "left" ? -300 : 300,
        behavior: "smooth",
      });
    }
  };

  useEffect(() => {
    const fetchskills = async () => {
      try {
        const response = await api.get("skills/college-skills/");
        const newProjects = [];
        const newNotes = [];

        for (let inner of response.data) {
          if (inner.skill === "project") {
            newProjects.push(inner);
          } else {
            newNotes.push(inner);
          }
        }

        setProjects(newProjects);
        setNotes(newNotes);
      } catch (error) {
        // console.log("Error fetching skills:", error);
        setSnackbarMessage("Failed to load skills data");
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      }
    };
    fetchskills();
  }, []);

  const filteredProjects = projects.filter(
    (p) =>
      (departmentFilter === "all" ||
        p.department.toLowerCase() === departmentFilter) &&
      p.name.toLowerCase().includes(search.toLowerCase())
  );

  const filteredNotes = notes.filter(
    (n) =>
      (departmentFilter === "all" ||
        n.department.toLowerCase() === departmentFilter) &&
      n.name.toLowerCase().includes(search.toLowerCase())
  );
  
const handledownload = (item) => {
  if (!item.pdf) {
    console.error("No PDF available");
    return;
  }

  const previewUrl = getPreviewFileUrl(item.pdf);

  if (!previewUrl) {
    console.error("Invalid PDF URL");
    return;
  }

  // Browser-native preview
  window.open(previewUrl, "_blank", "noopener,noreferrer");
};
  // List of departments
  const departments = ["cse", "ece", "me", "ee", "ce"];

  // If "All" selected → show all with data; else only show the selected one
  const departmentsToShow =
    departmentFilter === "all" ? departments : [departmentFilter];

  return (
    <Box sx={{ display: "flex", flexDirection: "column", minHeight: embedded ? "auto" : "100vh" }}>
      {!embedded && <LogoutNavbar sidebarItems={isMobile ? sidebarItems : []} />}
      <Box sx={{ display: "flex", flexGrow: 1 }}>
        {/* Only show sidebar on desktop */}
        {!embedded && !isMobile && <GlobalSidebar hideManagement />}
        
        <Box component="main" sx={{ 
          flexGrow: 1, 
          overflowX: "hidden",
          backgroundColor: embedded ? 'transparent' : "#dfdfdfff",
          background: embedded ? 'transparent' : "linear-gradient(135deg, rgba(9, 44, 12, 0.30) 0%, rgba(9, 53, 44, 0.30) 100%)",

        }}>
          <Box
            sx={{
              p: embedded ? 0 : { xs: 2, md: 4 },
              position: "relative",
              overflow: "hidden",
            }}
          >
            {!embedded && <BackButton sx={{ mb: 2 , backgroundColor: "#dfdfdff", color: "#000" }} />}
            {/* Header */}
            <Box sx={{ mb: 4, textAlign: "center" }}>
              <Typography
                variant="h4"
                fontWeight="bold"
                sx={{
                  background:
                    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                  WebkitBackgroundClip: "text", 
                  backgroundColor:
                    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                  WebkitTextFillColor: "transparent",
                  fontSize: { xs: "1.8rem", md: "2.5rem" }
                }}
              >
                Explore Skills & Projects
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "rgba(0, 0, 0, 1)" }}
              >
                Browse, filter, and view student projects and notes.
              </Typography>
            </Box>

            {/* Filters */}
            <Paper
              elevation={6}
              sx={{
                p: 2,
                mb: 6,
                display: "flex",
                gap: 2,
                flexDirection: { xs: "column", sm: "row" },
                alignItems: "center",
                borderRadius: 4,
                bgcolor: "rgba(255, 255, 255, 0.23)",
                backdropFilter: "blur(5px)",
                maxWidth: 900,
                margin: "0 auto",
                color: "black",
              }}
            >
              <FormControl sx={{ minWidth: 150, flex: { xs: 1, sm: "initial" } }}>
                <InputLabel
                  id="skills-dept-label"
                  shrink
                  sx={{ color: "#030303ff" }}
                >
                  Department
                </InputLabel>
                <Select
                  labelId="skills-dept-label"
                  id="skills-dept"
                  value={departmentFilter}
                  onChange={(e) => {
                    setDepartmentFilter(e.target.value);
                  }}
                  label="Department"
                  displayEmpty
                  renderValue={(selected) => {
                    const v = String(selected || "all").toLowerCase();
                    if (v === "all") return "All";
                    return v.toUpperCase();
                  }}
                  sx={{
                    backgroundColor: "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                    "& .MuiOutlinedInput-notchedOutline": { borderColor: "#000" },
                    "& .MuiSvgIcon-root": { color: "#000" },
                  }}
                >
                  <MenuItem value="all">All</MenuItem>
                  <MenuItem value="cse">CSE</MenuItem>
                  <MenuItem value="ece">ECE</MenuItem>
                  <MenuItem value="me">ME</MenuItem>
                  <MenuItem value="ee">EE</MenuItem>
                  <MenuItem value="ce">CE</MenuItem>
                </Select>
              </FormControl>
              <TextField
                label="Search by Name"
                variant="outlined"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 150,
                  "& .MuiInputLabel-root": { color: "#000" },
                  "& .MuiOutlinedInput-root": {
                    color: "#000",
                    "& fieldset": { borderColor: "#000" },
                  },
                }}
              />
            </Paper>

            {/* Projects Section */}
            <Typography
              variant="h5"
              fontWeight="bold"
              mb={4}
              sx={{
                textAlign: "center",
                background:
                  "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                marginTop: "15px",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
            >
              PROJECTS
            </Typography>

            {departmentsToShow.map((dept) => {
              const deptProjects = filteredProjects.filter(
                (p) => p.department.toLowerCase() === dept
              );

              // Skip department if All selected and no data
              if (departmentFilter === "all" && deptProjects.length === 0) {
                return null;
              }

              return (
                <Box key={dept} mb={6}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      ml: { xs: 0, sm: 6 },
                      background:
                        "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "inline-block",
                    }}
                  >
                    {dept.toUpperCase()} PROJECTS
                  </Typography>

                  {deptProjects.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        color: "#555",
                        fontStyle: "italic",
                      }}
                    >
                      No Projects Found
                    </Typography>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        onClick={() => scroll(projectRefs[dept], "left")}
                        sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}
                      >
                        <ArrowBackIosNewIcon />
                      </IconButton>

                      <Box
                        ref={projectRefs[dept]}
                        sx={{
                          display: "flex",
                          gap: 2,
                          overflowX: "auto",
                          flex: 1,
                          scrollBehavior: "smooth",
                          py: 1,
                          px: { xs: 1, sm: 0 },
                        }}
                      >
                        {deptProjects.map((project) => (
                          <motion.div
                            key={project.id}
                            whileHover={{ scale: 0.95 }}
                            style={{ width: "clamp(240px, 80vw, 280px)", flexShrink: 0 }}
                          >
                            <Card
                              sx={{
                                borderRadius: "20px",
                                overflow: "hidden",
                                boxShadow: "0 15px 40px rgba(0,0,0,0.2)",
                                backdropFilter: "blur(15px)",
                                background: "rgba(255, 255, 255, 0.96)",
                                border: "1px solid rgba(255, 255, 255, 0.91)",
                              }}
                            >
                              <CardMedia
                                component="img"
                                height="160"
                                image={getFullImageUrl(project.image)}
                                alt={project.name}
                              />
                              <CardContent>
                                <Typography variant="h6" fontWeight="bold" noWrap>
                                  {project.name}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                  noWrap
                                >
                                  {project.description}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  display="block"
                                  mt={1}
                                >
                                  Dept: {project.department.toUpperCase()}
                                </Typography>
                                <Typography variant="caption" display="block">
                                  By: {project.owner_username}
                                </Typography>
                                <Button
                                  onClick={() => handledownload(project)}
                                  variant="contained"
                                  size="small"
                                  sx={{
                                    mt: 2,
                                    borderRadius: "12px",
                                    background:
                                      "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                                    boxShadow: "0 8px 25px rgba(0,198,255,0.3)",
                                    "&:hover": {
                                      transform: "scale(1.05)",
                                      boxShadow: "0 12px 35px rgba(9, 49, 1, 0.7)"
                                    },
                                  }}
                                >
                                  DOWNLOAD
                                </Button>
                              </CardContent>
                            </Card>
                          </motion.div>
                        ))}
                      </Box>

                      <IconButton
                        onClick={() => scroll(projectRefs[dept], "right")}
                        sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}

            {/* Notes Section */}
            <Typography
              variant="h5"
              fontWeight="bold"
              mb={2}
              sx={{
                textAlign: "center",
                background:
                  "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
            >
              NOTES
            </Typography>

            {departmentsToShow.map((dept) => {
              const deptNotes = filteredNotes.filter(
                (n) => n.department.toLowerCase() === dept
              );

              if (departmentFilter === "all" && deptNotes.length === 0) {
                return null;
              }

              return (
                <Box key={dept} mb={6}>
                  <Typography
                    variant="h6"
                    fontWeight="bold"
                    mb={2}
                    sx={{
                      ml: { xs: 0, sm: 6 },
                      background:
                        "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      display: "inline-block",
                    }}
                  >
                    {dept.toUpperCase()} NOTES
                  </Typography>

                  {deptNotes.length === 0 ? (
                    <Typography
                      variant="body2"
                      sx={{
                        textAlign: "center",
                        color: "#555",
                        fontStyle: "italic",
                      }}
                    >
                      NO NOTES FOUND
                    </Typography>
                  ) : (
                    <Box display="flex" alignItems="center" gap={1}>
                      <IconButton
                        onClick={() => scroll(notesRefs[dept], "left")}
                        sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}
                      >
                        <ArrowBackIosNewIcon />
                      </IconButton>

                      <Box
                        ref={notesRefs[dept]}
                        sx={{
                          display: "flex",
                          gap: 2,
                          overflowX: "auto",
                          flex: 1,
                          scrollBehavior: "smooth",
                          py: 1,
                          px: { xs: 1, sm: 0 },
                        }}
                      >
                        {deptNotes.map((note) => (
                        <motion.div
                          key={note.id}
                          whileHover={{ scale: 0.95 }}
                            style={{ width: "clamp(240px, 80vw, 280px)", flexShrink: 0 }}
                        >
                          <Card
                            sx={{
                              borderRadius: "20px",
                              overflow: "hidden",
                              boxShadow: "0 15px 40px rgba(0,0,0,0.2)",
                              backdropFilter: "blur(15px)",
                              background: "rgba(255, 255, 255, 0.89)",
                              border: "1px solid rgba(255, 255, 255, 0.84)",
                            }}
                          >
                            {/* ✅ Added Image for Notes */}
                            <CardMedia
                              component="img"
                              height="160"
                              image={getFullImageUrl(note.image)}
                              alt={note.name}
                            />

                            <CardContent>
                              <Typography variant="h6" fontWeight="bold" noWrap>
                                {note.name}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" noWrap>
                                {note.description}
                              </Typography>
                              <Typography variant="caption" display="block" mt={1}>
                                Dept: {note.department.toUpperCase()}
                              </Typography>
                              <Typography variant="caption" display="block">
                                By: {note.owner_username}
                              </Typography>
                              <Button
                                onClick={() => handledownload(note)}
                                variant="contained"
                                size="small"
                                sx={{
                                  mt: 2,
                                  borderRadius: "12px",
                                  background:
                                    "linear-gradient(135deg, #0d2a44 0%, #016f57 30%, #016f57 70%, #016f57 100%)",
                                  boxShadow: "0 8px 25px rgba(0,198,255,0.3)",
                                  "&:hover": {
                                    transform: "scale(1.05)",
                                    boxShadow: "0 12px 35px rgba(9, 49, 1, 0.7)"
                                  },
                                }}
                              >
                                DOWNLOAD
                              </Button>
                            </CardContent>
                          </Card>
                        </motion.div>
                      ))}

                      </Box>

                      <IconButton
                        onClick={() => scroll(notesRefs[dept], "right")}
                        sx={{ color: "#000", display: { xs: "none", sm: "inline-flex" } }}
                      >
                        <ArrowForwardIosIcon />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={3000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>

      {!embedded && (
        <div ref={footerRef}>
          <PostLoginFooter />
        </div>
      )}
    </Box>
  );
}